import React, { useState, useEffect, useRef, useCallback } from 'react';
import JSZip from 'jszip';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Download, FileCode2, Loader2, ChevronDown, ChevronRight, ChevronLeft, Image as ImageIcon, ListChecks, X, ExternalLink, Sparkles, Send, MessageSquare } from 'lucide-react';
import { api } from '../../services/api';
import { getDeviceImages, DeviceImageMetadata } from '../../services/deviceImages';
import { sessionTasksService, projectTasksService } from '../../services/sessionTasksService';
import { exportSessionHTML } from '../../utils/exportSessionHTML';
import { ProductRequirements } from '../../services/workshop';
import type { SessionSummaryData } from '../SessionSummaryPanel';
import type { SessionTaskSummary } from '../../types/session-task.types';
import type { BackgroundInfo } from './SessionSetupView';
import type { DeepDiveMessage } from '../../services/n8n';

type OutputMode = 'zip' | 'html';

interface ChallengeArea {
  areaId: string;
  title: string;
  summary: string;
  keyTopics: string[];
  relevantPersonas: string[];
}

interface TaskOutputItem {
  taskName: string;
  workflowId: string;
  outputs: Array<{ type: string; content: any; source: string }>;
}

interface ExportPanelProps {
  sessionId: string;
  projectId?: string;
  sessionName?: string;
  requirements: ProductRequirements | null;
  removedPersonaIds: Set<string>;
  removedRequirementIds: Set<string>;
  backgroundInfo?: BackgroundInfo;
  goals?: string[];
  designContext?: string;
}

// ── Reasoning leak patterns to strip from AI-generated report sections ────────

const REASONING_PATTERNS = [
  /^now i have sufficient/i,
  /^let me compile/i,
  /^i will now/i,
  /^let me now/i,
  /^i'll now/i,
  /^i now have/i,
];

function stripReasoningLines(content: string): string {
  return content
    .split('\n')
    .filter(line => !REASONING_PATTERNS.some(p => p.test(line.trim())))
    .join('\n')
    .trim();
}

// ── PRD builder ───────────────────────────────────────────────────────────────

function buildPRD(opts: {
  sessionName?: string;
  exportedAt: string;
  backgroundInfo?: BackgroundInfo;
  goals?: string[];
  designContext?: string;
  challengeAreas?: ChallengeArea[];
  requirements: ProductRequirements | null;
  decisions: Array<{ statement: string; editedStatement?: string; topic?: string; editedTopic?: string; status: string }>;
  reportSections: Array<{ id: string; title: string; content: string | null }>;
}): string {
  const lines: string[] = [];
  const date = new Date(opts.exportedAt).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' });

  lines.push('# Product Requirements Document');
  lines.push('');
  if (opts.sessionName) lines.push(`**Session:** ${opts.sessionName}`);
  lines.push(`**Exported:** ${date}`);
  lines.push('');
  lines.push('---');
  lines.push('');

  // 1. Project Context
  lines.push('## 1. Project Context');
  lines.push('');
  if (opts.backgroundInfo?.customerName) lines.push(`**Customer:** ${opts.backgroundInfo.customerName}`);
  if (opts.backgroundInfo?.industry) lines.push(`**Industry:** ${opts.backgroundInfo.industry}`);
  lines.push('');
  if (opts.backgroundInfo?.context) {
    lines.push('### Workshop Background');
    lines.push('');
    lines.push(opts.backgroundInfo.context);
    lines.push('');
  }
  if (opts.goals?.length) {
    lines.push('### Workshop Goals');
    lines.push('');
    for (const g of opts.goals) lines.push(`- ${g}`);
    lines.push('');
  }
  if (opts.designContext) {
    lines.push('### Design Context');
    lines.push('');
    lines.push(opts.designContext);
    lines.push('');
  }
  lines.push('---');
  lines.push('');

  // 2. Challenge Areas
  const areas = opts.challengeAreas ?? [];
  if (areas.length) {
    lines.push('## 2. Challenge Areas');
    lines.push('');
    for (const area of areas) {
      lines.push(`### ${area.title}`);
      lines.push('');
      if (area.summary) { lines.push(area.summary); lines.push(''); }
      if (area.keyTopics?.length) lines.push(`**Key Topics:** ${area.keyTopics.join(', ')}`);
      if (area.relevantPersonas?.length) lines.push(`**Relevant Personas:** ${area.relevantPersonas.join(', ')}`);
      lines.push('');
    }
    lines.push('---');
    lines.push('');
  }

  // 3. Key Decisions
  const accepted = opts.decisions.filter(d => d.status === 'accepted');
  if (accepted.length) {
    lines.push('## 3. Key Decisions');
    lines.push('');
    accepted.forEach((d, i) => {
      const text = d.editedStatement || d.statement;
      const topic = d.editedTopic || d.topic;
      lines.push(`${i + 1}. ${text}${topic ? ' *(' + topic + ')*' : ''}`);
    });
    lines.push('');
    lines.push('---');
    lines.push('');
  }

  // 4. User Personas
  const personas = opts.requirements?.personas ?? [];
  // Deduplicate user stories by exact story text
  const rawStories = opts.requirements?.userStories ?? [];
  const seenStories = new Set<string>();
  const userStories = rawStories.filter(s => {
    if (seenStories.has(s.story)) return false;
    seenStories.add(s.story);
    return true;
  });

  if (personas.length) {
    lines.push('## 4. User Personas');
    lines.push('');
    for (const p of personas) {
      lines.push(`### ${p.name}${p.role && p.role !== p.name ? ' — ' + p.role : ''}`);
      lines.push('');
      if (p.description) { lines.push(p.description); lines.push(''); }
      if (p.goals?.length) {
        lines.push('**Goals:**');
        for (const g of p.goals) lines.push(`- ${g}`);
        lines.push('');
      }
      if (p.painPoints?.length) {
        lines.push('**Pain Points:**');
        for (const pp of p.painPoints) lines.push(`- ${pp}`);
        lines.push('');
      }
    }
    lines.push('---');
    lines.push('');
  }

  // 5. User Stories
  if (userStories.length) {
    lines.push('## 5. User Stories');
    lines.push('');
    userStories.forEach((s, i) => {
      lines.push(`### US-${String(i + 1).padStart(3, '0')}`);
      lines.push('');
      lines.push(`**Story:** ${s.story}`);
      if (s.persona) lines.push(`**Persona:** ${s.persona}`);
      lines.push(`**Priority:** ${s.priority}`);
      if (s.acceptanceCriteria?.length) {
        lines.push('');
        lines.push('**Acceptance Criteria:**');
        for (const ac of s.acceptanceCriteria) lines.push(`- [ ] ${ac}`);
      }
      lines.push('');
    });
    lines.push('---');
    lines.push('');
  }

  // 6. Non-Functional Requirements
  const nfrs = opts.requirements?.nonFunctionalRequirements ?? [];
  if (nfrs.length) {
    const NFR_CATEGORIES = ['performance', 'security', 'scalability', 'availability', 'usability', 'maintainability', 'compliance'] as const;
    lines.push('## 6. Non-Functional Requirements');
    lines.push('');
    for (const cat of NFR_CATEGORIES) {
      const catNfrs = nfrs.filter(n => n.category === cat);
      if (!catNfrs.length) continue;
      lines.push(`### ${cat.charAt(0).toUpperCase() + cat.slice(1)}`);
      lines.push('');
      for (const n of catNfrs) {
        lines.push(`- **${n.title}** (${n.priority})`);
        if (n.description) lines.push(`  ${n.description}`);
        if (n.acceptanceCriteria?.length) {
          for (const ac of n.acceptanceCriteria) lines.push(`  - [ ] ${ac}`);
        }
      }
      lines.push('');
    }
    lines.push('---');
    lines.push('');
  }

  // 7. Generated Report Sections (strip leaked AI reasoning)
  const generatedSections = opts.reportSections
    .filter(s => s.content)
    .map(s => ({ ...s, content: stripReasoningLines(s.content!) }))
    .filter(s => s.content.length > 0);

  if (generatedSections.length) {
    lines.push('## 7. Analysis & Report');
    lines.push('');
    for (const s of generatedSections) {
      lines.push(`### ${s.title}`);
      lines.push('');
      lines.push(s.content);
      lines.push('');
    }
    lines.push('---');
    lines.push('');
  }

  lines.push('*Generated by Version 1\'s Co-Creation Platform*');
  return lines.join('\n');
}

// ── Markdown renderer for chat ────────────────────────────────────────────────

const mdComponents: Components = {
  p: ({ children }) => <p className="text-sm leading-relaxed mb-2 last:mb-0">{children}</p>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-slate-800 mt-2 mb-1">{children}</h3>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-4 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-4 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  code: ({ children }) => <code className="bg-slate-200/60 text-slate-800 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
};

// ── Pre-flight issue detector (no AI needed) ──────────────────────────────────

interface PreflightIssue {
  type: 'duplicate' | 'thin_ac' | 'no_ac' | 'malformed_nfr_ac';
  message: string;
}

function detectPreflightIssues(requirements: ProductRequirements | null): PreflightIssue[] {
  if (!requirements) return [];
  const issues: PreflightIssue[] = [];

  // Duplicate stories
  const storyCounts = new Map<string, number>();
  for (const s of requirements.userStories) {
    storyCounts.set(s.story, (storyCounts.get(s.story) ?? 0) + 1);
  }
  const dupCount = [...storyCounts.values()].filter(c => c > 1).length;
  if (dupCount > 0) {
    issues.push({ type: 'duplicate', message: `${dupCount} duplicate user ${dupCount === 1 ? 'story' : 'stories'} detected — will be deduplicated in export` });
  }

  // Stories with no ACs
  const noAc = requirements.userStories.filter(s => !s.acceptanceCriteria?.length);
  if (noAc.length) {
    issues.push({ type: 'no_ac', message: `${noAc.length} ${noAc.length === 1 ? 'story has' : 'stories have'} no acceptance criteria` });
  }

  // Thin ACs (< 10 words)
  const thinAc = requirements.userStories.filter(s =>
    s.acceptanceCriteria?.some(ac => ac.split(' ').filter(Boolean).length < 10)
  );
  if (thinAc.length) {
    issues.push({ type: 'thin_ac', message: `${thinAc.length} ${thinAc.length === 1 ? 'story has' : 'stories have'} acceptance criteria too short to be testable` });
  }

  // NFR ACs that copy their description
  const badNfrs = (requirements.nonFunctionalRequirements ?? []).filter(n =>
    n.description && n.acceptanceCriteria?.some(ac => n.description && ac.includes(n.description.slice(0, 40)))
  );
  if (badNfrs.length) {
    issues.push({ type: 'malformed_nfr_ac', message: `${badNfrs.length} NFR ${badNfrs.length === 1 ? 'has an AC that copies' : 'have ACs that copy'} their description text` });
  }

  return issues;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

const TaskGroupCheckbox: React.FC<{
  someSelected: boolean;
  allSelected: boolean;
  onToggle: () => void;
}> = ({ someSelected, allSelected, onToggle }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = someSelected; }, [someSelected]);
  return <input ref={ref} type="checkbox" checked={allSelected} onChange={onToggle} className="h-4 w-4 accent-primary shrink-0" />;
};

const ImageGroupCheckbox: React.FC<{
  someSelected: boolean;
  allSelected: boolean;
  onToggle: () => void;
}> = ({ someSelected, allSelected, onToggle }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = someSelected; }, [someSelected]);
  return <input ref={ref} type="checkbox" checked={allSelected} onChange={onToggle} className="h-4 w-4 accent-primary shrink-0" />;
};

// ── Main component ─────────────────────────────────────────────────────────────

const POLL_INTERVAL = 4000;

const ExportPanel: React.FC<ExportPanelProps> = ({
  sessionId,
  projectId,
  sessionName,
  requirements,
  removedPersonaIds,
  removedRequirementIds,
  backgroundInfo,
  goals,
  designContext,
}) => {
  const [outputMode, setOutputMode] = useState<OutputMode>('zip');
  const [exporting, setExporting] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Simple toggles
  const [includeTranscript, setIncludeTranscript] = useState(true);
  const [includeRequirements, setIncludeRequirements] = useState(true);
  const [includeBuildWithAI, setIncludeBuildWithAI] = useState(true);

  // Challenge areas
  const [challengeAreas, setChallengeAreas] = useState<ChallengeArea[]>([]);

  // Tasks
  const [tasks, setTasks] = useState<SessionTaskSummary[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [tasksExpanded, setTasksExpanded] = useState(true);

  // Images
  const [images, setImages] = useState<DeviceImageMetadata[]>([]);
  const [loadingImages, setLoadingImages] = useState(true);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());
  const [imagesExpanded, setImagesExpanded] = useState(true);

  // PRD review chat
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewMessages, setReviewMessages] = useState<DeepDiveMessage[]>([]);
  const [reviewInput, setReviewInput] = useState('');
  const [reviewAnalyzing, setReviewAnalyzing] = useState(false);
  const [currentPrd, setCurrentPrd] = useState<string>('');
  const reviewPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const knownReviewMessageIds = useRef<Set<string>>(new Set());
  const reviewChatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    sessionTasksService
      .listTasks(sessionId, { status: ['completed'] })
      .then(t => {
        const withOutputs = t.filter(task =>
          task.executionId &&
          task.templateId !== 'image-generation' &&
          task.templateId !== 'session-task-generate-session-image'
        );
        setTasks(withOutputs);
        setSelectedTaskIds(new Set(withOutputs.filter(t => t.taggedForExport).map(t => t.workflowId)));
      })
      .catch(() => {})
      .finally(() => setLoadingTasks(false));

    getDeviceImages(sessionId)
      .then(imgs => {
        setImages(imgs);
        setSelectedImageIds(new Set(imgs.filter(img => img.taggedForExport).map(img => img.imageId)));
      })
      .catch(() => {})
      .finally(() => setLoadingImages(false));

    api.get(`/api/sessions/${sessionId}/challenge-areas`)
      .then(res => setChallengeAreas(res.data.challengeAreas || []))
      .catch(() => {});
  }, [sessionId]);

  // Scroll review chat to bottom
  useEffect(() => {
    reviewChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [reviewMessages]);

  // Poll review messages when open and analyzing
  const pollReviewMessages = useCallback(async () => {
    try {
      const res = await api.get(`/api/sessions/${sessionId}/prd-review/messages`);
      const all: DeepDiveMessage[] = res.data.messages || [];
      const fresh = all.filter(m => !knownReviewMessageIds.current.has(m.id));
      if (fresh.length) {
        fresh.forEach(m => knownReviewMessageIds.current.add(m.id));
        setReviewMessages(prev => [...prev, ...fresh]);
        setReviewAnalyzing(false);
      }
    } catch { /* ignore */ }
  }, [sessionId]);

  useEffect(() => {
    if (reviewOpen && reviewAnalyzing) {
      reviewPollRef.current = setInterval(pollReviewMessages, POLL_INTERVAL);
    } else {
      if (reviewPollRef.current) { clearInterval(reviewPollRef.current); reviewPollRef.current = null; }
    }
    return () => { if (reviewPollRef.current) clearInterval(reviewPollRef.current); };
  }, [reviewOpen, reviewAnalyzing, pollReviewMessages]);

  const handleSendReview = async () => {
    const text = reviewInput.trim();
    if (!text || reviewAnalyzing) return;

    const userMsg: DeepDiveMessage = {
      id: `user-${Date.now()}`,
      agentType: 'persona',
      personaId: 'user',
      personaName: 'You',
      message: text,
      timestamp: new Date().toISOString(),
    };
    setReviewMessages(prev => [...prev, userMsg]);
    setReviewInput('');
    setReviewAnalyzing(true);

    try {
      const res = await api.post(`/api/sessions/${sessionId}/prd-review/chat`, {
        prdContent: currentPrd,
        userMessage: text,
        messages: [...reviewMessages, userMsg].map(m => ({ role: m.agentType === 'persona' ? 'user' : 'assistant', content: m.message })),
      });
      const { requestId } = res.data;
      if (requestId) {
        knownReviewMessageIds.current.add(`${requestId}-user`);
      }
    } catch {
      setReviewAnalyzing(false);
    }
  };

  const handleClearReview = async () => {
    setReviewMessages([]);
    knownReviewMessageIds.current.clear();
    api.delete(`/api/sessions/${sessionId}/prd-review/messages`).catch(() => {});
  };

  const filteredRequirements = requirements
    ? {
        ...requirements,
        personas: requirements.personas.filter(p => !removedPersonaIds.has(p.id)),
        userStories: requirements.userStories.filter(s => !removedRequirementIds.has(s.id)),
      }
    : null;

  const anySelected =
    includeTranscript ||
    includeRequirements ||
    selectedTaskIds.size > 0 ||
    selectedImageIds.size > 0 ||
    (includeBuildWithAI && !!projectId && outputMode === 'zip');

  // ── Group toggle helpers ───────────────────────────────────────────────────

  const allTasksSelected = tasks.length > 0 && selectedTaskIds.size === tasks.length;
  const someTasksSelected = selectedTaskIds.size > 0 && selectedTaskIds.size < tasks.length;
  const allImagesSelected = images.length > 0 && selectedImageIds.size === images.length;
  const someImagesSelected = selectedImageIds.size > 0 && selectedImageIds.size < images.length;

  // Lightbox
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImage, setLightboxImage] = useState<DeviceImageMetadata | null>(null);

  const openLightbox = (img: DeviceImageMetadata) => {
    const idx = images.findIndex(i => i.imageId === img.imageId);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxImage(img);
  };

  useEffect(() => {
    if (!lightboxImage) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setLightboxImage(null); return; }
      if (e.key === 'ArrowLeft') {
        setLightboxIndex(prev => {
          const next = (prev - 1 + images.length) % images.length;
          setLightboxImage(images[next]);
          return next;
        });
      }
      if (e.key === 'ArrowRight') {
        setLightboxIndex(prev => {
          const next = (prev + 1) % images.length;
          setLightboxImage(images[next]);
          return next;
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxImage, images]);

  // Task output preview
  const [previewingTaskId, setPreviewingTaskId] = useState<string | null>(null);

  const openTaskPreview = async (task: SessionTaskSummary) => {
    if (!task.executionId || previewingTaskId) return;
    setPreviewingTaskId(task.workflowId);
    try {
      const execution = await sessionTasksService.getTaskExecution(sessionId, task.workflowId, task.executionId);
      const outputs = Array.isArray(execution.outputs) ? execution.outputs : [];
      for (const { type, content } of outputs) {
        if (type === 'html_artifact' && content?.presignedUrl) {
          window.open(content.presignedUrl, '_blank', 'noopener');
          return;
        }
        if (content?.s3Key) {
          const pid = content.s3Key.split('/')[1];
          if (pid) {
            const { data } = await api.get(`/api/projects/${pid}/s3/download?key=${encodeURIComponent(content.s3Key)}`);
            window.open(data.downloadUrl, '_blank', 'noopener');
            return;
          }
        }
      }
    } catch { /* ignore */ }
    finally { setPreviewingTaskId(null); }
  };

  const toggleAllTasks = () => {
    setSelectedTaskIds(allTasksSelected ? new Set() : new Set(tasks.map(t => t.workflowId)));
  };

  const toggleTask = (id: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllImages = () => {
    setSelectedImageIds(allImagesSelected ? new Set() : new Set(images.map(i => i.imageId)));
  };

  const toggleImage = (id: string) => {
    setSelectedImageIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Data helpers ──────────────────────────────────────────────────────────

  const fetchSummary = async (): Promise<SessionSummaryData | null> => {
    try {
      const res = await api.get<SessionSummaryData>(`/api/sessions/${sessionId}/summary?variant=structured`);
      if (res.data) return res.data;
    } catch { /* fall through */ }
    try {
      const res = await api.get<SessionSummaryData>(`/api/sessions/${sessionId}/summary?variant=full`);
      if (res.data) return res.data;
    } catch { /* ignore */ }
    return null;
  };

  const fetchSelectedTaskOutputs = async (): Promise<TaskOutputItem[]> => {
    const selectedTasks = tasks.filter(t => selectedTaskIds.has(t.workflowId) && t.executionId);
    const results = await Promise.all(
      selectedTasks.map(async (task) => {
        try {
          const execution = await sessionTasksService.getTaskExecution(
            sessionId,
            task.workflowId,
            task.executionId!
          );
          const outputs = Array.isArray(execution.outputs) ? execution.outputs : [];
          return { taskName: task.taskName, workflowId: task.workflowId, outputs } as TaskOutputItem;
        } catch {
          return null;
        }
      })
    );
    return results.filter((r): r is TaskOutputItem => r !== null);
  };

  const selectedImages = images.filter(img => selectedImageIds.has(img.imageId));

  // ── Export handlers ───────────────────────────────────────────────────────

  const handleHTMLExport = async () => {
    let taskOutputsForHTML: TaskOutputItem[] | undefined;
    let imagesForHTML: DeviceImageMetadata[] | undefined;

    if (selectedTaskIds.size > 0) {
      setStatusMsg('Fetching task outputs…');
      taskOutputsForHTML = await fetchSelectedTaskOutputs();
    }
    if (selectedImageIds.size > 0) {
      imagesForHTML = selectedImages;
    }

    setStatusMsg('Fetching session summary…');
    const summary = await fetchSummary();

    setStatusMsg('Building HTML…');
    await exportSessionHTML(
      sessionId,
      sessionName,
      includeRequirements ? filteredRequirements : null,
      summary,
      {
        includeTranscript,
        images: imagesForHTML?.length ? imagesForHTML : undefined,
        taskOutputs: taskOutputsForHTML?.length ? taskOutputsForHTML : undefined,
      }
    );
  };

  const handleZIPExport = async () => {
    const zip = new JSZip();

    if (includeTranscript) {
      setStatusMsg('Exporting transcript…');
      const { data } = await api.get(`/api/sessions/${sessionId}/transcript/export`);
      zip.file('transcript.json', JSON.stringify(data, null, 2));
    }

    if (includeRequirements && requirements) {
      setStatusMsg('Exporting requirements…');
      zip.file('requirements.json', JSON.stringify({
        sessionId,
        exportedAt: new Date().toISOString(),
        projectName: requirements.projectName,
        personas: filteredRequirements?.personas ?? [],
        userStories: filteredRequirements?.userStories ?? [],
        nonFunctionalRequirements: filteredRequirements?.nonFunctionalRequirements ?? [],
      }, null, 2));
    }

    if (selectedImageIds.size > 0) {
      setStatusMsg('Downloading images…');
      const folder = zip.folder('images')!;
      await Promise.all(selectedImages.map(async (img) => {
        try {
          const { data: blob } = await api.get(
            `/api/device-images/${sessionId}/${img.imageId}/download`,
            { responseType: 'blob' }
          );
          const ext = (img.s3Key.split('.').pop() || 'jpg').replace(/\?.*$/, '');
          const label = (img.captureLabel || img.imageId).replace(/[/\\?%*:|"<>]/g, '-');
          folder.file(`${label}.${ext}`, blob);
        } catch { /* skip */ }
      }));
    }

    if (selectedTaskIds.size > 0) {
      setStatusMsg('Exporting task outputs…');
      const taskOutputs = await fetchSelectedTaskOutputs();
      const tasksFolder = zip.folder('tasks')!;

      for (const { taskName, workflowId, outputs } of taskOutputs) {
        const safeName = `${taskName.replace(/[/\\?%*:|"<>]/g, '-')}-${workflowId.slice(0, 8)}`;
        const taskFolder = tasksFolder.folder(safeName)!;
        const typeCount: Record<string, number> = {};

        for (const output of outputs) {
          try {
            const { type, content } = output;
            typeCount[type] = (typeCount[type] || 0) + 1;
            const suffix = typeCount[type] > 1 ? `-${typeCount[type]}` : '';

            if (type === 'html_artifact' && content?.presignedUrl) {
              const res = await fetch(content.presignedUrl);
              const baseName = content.fileName || 'output';
              taskFolder.file(`${baseName}${suffix}.html`, await res.text());

            } else if (type === 'scaffold_bundle' && content?.s3Key) {
              const pid = content.s3Key.split('/')[1];
              if (pid) {
                const { data: urlData } = await api.get(`/api/projects/${pid}/s3/download?key=${encodeURIComponent(content.s3Key)}`);
                const res = await fetch(urlData.downloadUrl);
                taskFolder.file(content.fileName || 'bundle.zip', await res.blob());
              }

            } else if (type === 'diagram_code' && content) {
              taskFolder.file('diagram.mermaid', typeof content === 'string' ? content : JSON.stringify(content));

            } else if (type === 'markdown' && content?.text) {
              taskFolder.file(`${type}.md`, content.text);

            } else if (content?.s3Key) {
              const pid = content.s3Key.split('/')[1];
              if (pid) {
                const { data: urlData } = await api.get(`/api/projects/${pid}/s3/download?key=${encodeURIComponent(content.s3Key)}`);
                const res = await fetch(urlData.downloadUrl);
                const isZip = content.s3Key.endsWith('.zip');
                taskFolder.file(
                  isZip ? (content.fileName || `${type}.zip`) : `${content.title || type}.md`,
                  isZip ? await res.blob() : await res.text()
                );
              }

            } else {
              taskFolder.file(`${type}.json`, JSON.stringify(content, null, 2));
            }
          } catch { /* skip */ }
        }
      }
    }

    if (includeBuildWithAI && projectId) {
      setStatusMsg('Fetching Build with AI bundle…');
      try {
        const projectTasks = await projectTasksService.listTasks(projectId);
        const buildTasks = projectTasks.filter(t =>
          t.templateId === 'session-task-build-with-ai' &&
          t.status === 'completed' &&
          (t.sourceSessionIds || []).includes(sessionId)
        );
        for (const task of buildTasks) {
          const detail = await projectTasksService.getTask(projectId, task.taskId);
          const scaffoldOutput = detail.outputs?.find(o => o.type === 'scaffold_bundle');
          if (scaffoldOutput?.content?.s3Key) {
            const { data: urlData } = await api.get(
              `/api/projects/${projectId}/s3/download?key=${encodeURIComponent(scaffoldOutput.content.s3Key)}`
            );
            const res = await fetch(urlData.downloadUrl);
            const name = (scaffoldOutput.content.fileName || `${task.taskName}.zip`).replace(/[/\\?%*:|"<>]/g, '-');
            zip.file(`build-with-ai/${name}`, await res.blob());
          }
        }
      } catch { /* skip */ }
    }

    // Build PRD (always included)
    setStatusMsg('Building PRD…');
    let prdMarkdown = '';
    try {
      const [decisionsRes, reportRes] = await Promise.all([
        api.get(`/api/sessions/${sessionId}/decisions`),
        projectId
          ? api.get(`/api/projects/${projectId}/report/state`, { params: { sessionId, scope: 'session' } })
          : Promise.resolve({ data: { sections: [] } }),
      ]);
      const decisions = decisionsRes.data?.decisions ?? [];
      const reportSections = (reportRes.data?.sections ?? []) as Array<{ id: string; title: string; content: string | null }>;
      prdMarkdown = buildPRD({
        sessionName,
        exportedAt: new Date().toISOString(),
        backgroundInfo,
        goals,
        designContext,
        challengeAreas,
        requirements: filteredRequirements,
        decisions,
        reportSections,
      });
      zip.file('PRD.md', prdMarkdown);
      setCurrentPrd(prdMarkdown);

      // Auto-save PRD to S3 (fire-and-forget)
      api.post(`/api/sessions/${sessionId}/prd/save`, { content: prdMarkdown }).catch(() => {});
    } catch { /* skip PRD on error */ }

    setStatusMsg('Generating ZIP…');
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-export-${sessionId.slice(0, 8)}-${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setStatusMsg('');
  };

  const handleExport = async () => {
    if (!anySelected) return;
    setExporting(true);
    setErrorMsg('');
    setStatusMsg('Preparing…');
    try {
      outputMode === 'html' ? await handleHTMLExport() : await handleZIPExport();
    } catch (err) {
      console.error('Export failed:', err);
      setErrorMsg('Export failed. Please try again.');
    } finally {
      setExporting(false);
      setStatusMsg('');
    }
  };

  // ── Pre-flight issues ─────────────────────────────────────────────────────

  const preflightIssues = detectPreflightIssues(filteredRequirements);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-6 border-b border-slate-200 bg-white shrink-0">
        <h1 className="text-xl font-bold text-slate-900">Export Session</h1>
        <p className="text-sm text-slate-500 mt-1">Select content to include and choose an output format.</p>
      </div>

      <div className="flex-1 p-6 flex flex-col gap-6">
        <div>
          <h2 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-3">Content</h2>
          <div className="flex flex-col gap-2">

            {/* Transcript */}
            <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors">
              <input type="checkbox" checked={includeTranscript} onChange={() => setIncludeTranscript(v => !v)} className="mt-0.5 h-4 w-4 accent-primary shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">Transcript</p>
                <p className="text-xs text-slate-500 mt-0.5">Full session conversation as JSON</p>
              </div>
            </label>

            {/* Requirements */}
            <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors">
              <input type="checkbox" checked={includeRequirements} onChange={() => setIncludeRequirements(v => !v)} className="mt-0.5 h-4 w-4 accent-primary shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">User Stories & Personas</p>
                <p className="text-xs text-slate-500 mt-0.5">Captured requirements as JSON</p>
              </div>
            </label>

            {/* Task Outputs — expandable */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex items-center gap-3 p-3">
                {loadingTasks ? (
                  <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" />
                ) : (
                  <TaskGroupCheckbox someSelected={someTasksSelected} allSelected={allTasksSelected} onToggle={toggleAllTasks} />
                )}
                <button
                  onClick={() => setTasksExpanded(v => !v)}
                  className="flex-1 flex items-center justify-between text-left"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <ListChecks size={14} className="text-slate-400" />
                      Task Outputs
                      {!loadingTasks && tasks.length > 0 && (
                        <span className="text-[11px] font-normal text-slate-400">
                          {selectedTaskIds.size}/{tasks.length}
                        </span>
                      )}
                    </p>
                    {loadingTasks && <p className="text-xs text-slate-400 mt-0.5">Loading…</p>}
                    {!loadingTasks && tasks.length === 0 && <p className="text-xs text-slate-400 mt-0.5">No completed tasks</p>}
                  </div>
                  {!loadingTasks && tasks.length > 0 && (
                    tasksExpanded
                      ? <ChevronDown size={14} className="text-slate-400 shrink-0" />
                      : <ChevronRight size={14} className="text-slate-400 shrink-0" />
                  )}
                </button>
              </div>

              {!loadingTasks && tasksExpanded && tasks.length > 0 && (
                <div className="border-t border-slate-100">
                  {tasks.map(task => (
                    <div
                      key={task.workflowId}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        id={`task-${task.workflowId}`}
                        checked={selectedTaskIds.has(task.workflowId)}
                        onChange={() => toggleTask(task.workflowId)}
                        className="h-3.5 w-3.5 accent-primary shrink-0"
                      />
                      <label htmlFor={`task-${task.workflowId}`} className="text-xs text-slate-700 truncate flex-1 cursor-pointer">
                        {task.taskName}
                      </label>
                      <button
                        onClick={() => openTaskPreview(task)}
                        disabled={!!previewingTaskId}
                        className="shrink-0 text-slate-400 hover:text-primary disabled:opacity-40 transition-colors"
                        title="Open output in new tab"
                      >
                        {previewingTaskId === task.workflowId
                          ? <Loader2 size={12} className="animate-spin" />
                          : <ExternalLink size={12} />
                        }
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Images — expandable */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex items-center gap-3 p-3">
                {loadingImages ? (
                  <Loader2 size={16} className="animate-spin text-slate-400 shrink-0" />
                ) : (
                  <ImageGroupCheckbox someSelected={someImagesSelected} allSelected={allImagesSelected} onToggle={toggleAllImages} />
                )}
                <button
                  onClick={() => setImagesExpanded(v => !v)}
                  className="flex-1 flex items-center justify-between text-left"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <ImageIcon size={14} className="text-slate-400" />
                      Session Images
                      {!loadingImages && images.length > 0 && (
                        <span className="text-[11px] font-normal text-slate-400">
                          {selectedImageIds.size}/{images.length}
                        </span>
                      )}
                    </p>
                    {loadingImages && <p className="text-xs text-slate-400 mt-0.5">Loading…</p>}
                    {!loadingImages && images.length === 0 && <p className="text-xs text-slate-400 mt-0.5">No images captured</p>}
                  </div>
                  {!loadingImages && images.length > 0 && (
                    imagesExpanded
                      ? <ChevronDown size={14} className="text-slate-400 shrink-0" />
                      : <ChevronRight size={14} className="text-slate-400 shrink-0" />
                  )}
                </button>
              </div>

              {!loadingImages && imagesExpanded && images.length > 0 && (
                <div className="border-t border-slate-100 grid grid-cols-6 gap-1 p-2">
                  {images.map(img => {
                    const isChecked = selectedImageIds.has(img.imageId);
                    return (
                      <div
                        key={img.imageId}
                        className={`relative rounded overflow-hidden border-2 transition-colors ${isChecked ? 'border-primary' : 'border-transparent'}`}
                      >
                        <img
                          src={img.presignedUrl}
                          alt={img.captureLabel || img.imageId}
                          className="w-full aspect-square object-cover cursor-zoom-in"
                          onClick={() => openLightbox(img)}
                        />
                        <button
                          onClick={() => toggleImage(img.imageId)}
                          className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full border flex items-center justify-center transition-colors ${
                            isChecked
                              ? 'bg-primary border-primary'
                              : 'bg-black/40 border-white/70 hover:bg-black/60'
                          }`}
                        >
                          {isChecked && (
                            <svg width="5" height="5" viewBox="0 0 8 8" fill="none">
                              <path d="M1.5 4L3.5 6L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Build with AI (ZIP only, project-scoped) */}
            {projectId && outputMode === 'zip' && (
              <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-white cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors">
                <input type="checkbox" checked={includeBuildWithAI} onChange={() => setIncludeBuildWithAI(v => !v)} className="mt-0.5 h-4 w-4 accent-primary shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Build with AI Bundle</p>
                  <p className="text-xs text-slate-500 mt-0.5">Scaffold ZIP from project build tasks</p>
                </div>
              </label>
            )}
          </div>
        </div>

        {/* Output mode */}
        <div>
          <h2 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-3">Format</h2>
          <div className="flex flex-col gap-2">
            <label className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors" style={{ borderColor: outputMode === 'zip' ? 'var(--color-primary, #00c7c4)' : '#e2e8f0', background: outputMode === 'zip' ? 'rgba(0,199,196,0.04)' : 'white' }}>
              <input type="radio" name="outputMode" value="zip" checked={outputMode === 'zip'} onChange={() => setOutputMode('zip')} className="mt-0.5 h-4 w-4 accent-primary shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">ZIP Download</p>
                <p className="text-xs text-slate-500 mt-0.5">Individual files bundled into a ZIP archive for long-term storage.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors" style={{ borderColor: outputMode === 'html' ? 'var(--color-primary, #00c7c4)' : '#e2e8f0', background: outputMode === 'html' ? 'rgba(0,199,196,0.04)' : 'white' }}>
              <input type="radio" name="outputMode" value="html" checked={outputMode === 'html'} onChange={() => setOutputMode('html')} className="mt-0.5 h-4 w-4 accent-primary shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-800">HTML Report</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Single-file HTML with a built-in nav. Best for sharing.
                  {' '}<span className="text-amber-600 font-medium">Images use temporary links (valid ~1 hour).</span>
                </p>
              </div>
            </label>
          </div>
        </div>

        {errorMsg && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{errorMsg}</p>
        )}

        <button
          onClick={handleExport}
          disabled={exporting || !anySelected}
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: '#00c7c4', color: 'white' }}
        >
          {exporting ? (
            <><Loader2 size={16} className="animate-spin" />{statusMsg || 'Exporting…'}</>
          ) : outputMode === 'html' ? (
            <><FileCode2 size={16} />Export HTML</>
          ) : (
            <><Download size={16} />Download ZIP</>
          )}
        </button>

        {!anySelected && (
          <p className="text-xs text-slate-400 text-center -mt-3">Select at least one item.</p>
        )}

        {/* PRD Review Chat */}
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <button
            onClick={() => setReviewOpen(v => !v)}
            className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Sparkles size={15} className="text-primary shrink-0" />
              <span className="text-sm font-semibold text-slate-800">Review PRD with AI</span>
              {preflightIssues.length > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  {preflightIssues.length} {preflightIssues.length === 1 ? 'issue' : 'issues'}
                </span>
              )}
            </div>
            {reviewOpen
              ? <ChevronDown size={14} className="text-slate-400 shrink-0" />
              : <ChevronRight size={14} className="text-slate-400 shrink-0" />
            }
          </button>

          {reviewOpen && (
            <div className="border-t border-slate-100">
              {/* Pre-flight issues */}
              {preflightIssues.length > 0 && (
                <div className="px-4 py-3 bg-amber-50/60 border-b border-amber-100 flex flex-col gap-1.5">
                  {preflightIssues.map((issue, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-amber-800">
                      <span className="mt-0.5 shrink-0">⚠</span>
                      <span>{issue.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Chat area */}
              <div className="flex flex-col h-64">
                <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
                  {reviewMessages.length === 0 && !reviewAnalyzing && (
                    <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-400">
                      <MessageSquare size={22} />
                      <p className="text-xs text-center">
                        {currentPrd
                          ? 'Ask about the PRD quality, request rewrites, or check specific stories.'
                          : 'Export a ZIP first to load the PRD, then ask questions here.'}
                      </p>
                    </div>
                  )}
                  {reviewMessages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex flex-col gap-0.5 ${msg.agentType === 'persona' ? 'items-end' : 'items-start'}`}
                    >
                      <span className="text-[10px] text-slate-400 px-1">{msg.personaName}</span>
                      <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                        msg.agentType === 'persona'
                          ? 'bg-primary text-white rounded-br-sm'
                          : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                      }`}>
                        {msg.agentType === 'persona' ? (
                          <p className="text-sm">{msg.message}</p>
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {msg.message}
                          </ReactMarkdown>
                        )}
                      </div>
                    </div>
                  ))}
                  {reviewAnalyzing && (
                    <div className="flex items-start gap-2">
                      <div className="bg-slate-100 rounded-xl rounded-bl-sm px-3 py-2 flex items-center gap-2">
                        <Loader2 size={12} className="animate-spin text-slate-400" />
                        <span className="text-xs text-slate-500">Reviewing…</span>
                      </div>
                    </div>
                  )}
                  <div ref={reviewChatEndRef} />
                </div>

                {/* Input row */}
                <div className="border-t border-slate-100 px-3 py-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={reviewInput}
                    onChange={e => setReviewInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReview(); } }}
                    placeholder={currentPrd ? 'Ask about the PRD…' : 'Export ZIP first to enable chat'}
                    disabled={!currentPrd || reviewAnalyzing}
                    className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
                  />
                  {reviewMessages.length > 0 && (
                    <button
                      onClick={handleClearReview}
                      className="text-slate-400 hover:text-slate-600 transition-colors text-xs shrink-0"
                      title="Clear conversation"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={handleSendReview}
                    disabled={!currentPrd || !reviewInput.trim() || reviewAnalyzing}
                    className="shrink-0 p-1.5 rounded-lg bg-primary text-white disabled:opacity-40 transition-opacity"
                  >
                    <Send size={13} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/60 hover:text-white p-2 transition-colors"
          >
            <X size={22} />
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const next = (lightboxIndex - 1 + images.length) % images.length;
                  setLightboxIndex(next);
                  setLightboxImage(images[next]);
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2 transition-colors"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const next = (lightboxIndex + 1) % images.length;
                  setLightboxIndex(next);
                  setLightboxImage(images[next]);
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2 transition-colors"
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}

          <div
            className="flex flex-col items-center gap-3 px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.presignedUrl}
              alt={lightboxImage.captureLabel || lightboxImage.imageId}
              className="max-w-[80vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            {lightboxImage.captureLabel && (
              <p className="text-white/80 text-sm">{lightboxImage.captureLabel}</p>
            )}
            {images.length > 1 && (
              <p className="text-white/40 text-xs">{lightboxIndex + 1} / {images.length}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportPanel;
